import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import {
  createProvider,
  updateProvider,
  fetchProviderById,
  fetchNextProviderId,
} from "../../utils/api";

const maroon = "#500000";

const AddEditProvider = ({ open, onClose, onSave, provider }) => {
  const isEditMode = !!provider?.providerId;

  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    title: "",
    suffix: "",
    department: "",
    name: "",
    facility: "",
    providerId: "",
    facilityAddress: "",
    contactNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Load provider data if in edit mode
  useEffect(() => {
    const loadProviderData = async () => {
      if (isEditMode && provider?.providerId) {
        try {
          setLoading(true);
          const providerData = await fetchProviderById(provider.providerId);
          if (providerData) {
            setFormData({
              lastName: providerData.lastName || "",
              firstName: providerData.firstName || "",
              title: providerData.title || "",
              suffix: providerData.suffix || "",
              department: providerData.department || "",
              name: providerData.name || "",
              facility: providerData.facility || "",
              providerId: providerData.providerId || "",
              facilityAddress: providerData.facilityAddress || "",
              contactNumber: providerData.contactNumber || "",
            });
          }
        } catch (error) {
          console.error("Error loading provider:", error);
          setErrorMessage("Failed to load provider data");
        } finally {
          setLoading(false);
        }
      } else if (!isEditMode) {
        // Reset form for new provider
        setFormData({
          lastName: "",
          firstName: "",
          title: "",
          suffix: "",
          department: "",
          name: "",
          facility: "",
          providerId: "",
          facilityAddress: "",
          contactNumber: "",
        });
        setErrors({});
        setErrorMessage("");
        
        // Fetch next provider ID
        try {
          setLoading(true);
          const nextId = await fetchNextProviderId();
          setFormData((prev) => ({ ...prev, providerId: nextId }));
        } catch (error) {
          console.error("Error fetching next provider ID:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    if (open) {
      loadProviderData();
    }
  }, [open, isEditMode, provider]);

  const validate = () => {
    const newErrors = {};

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    }

    const pid = formData.providerId.trim();
    if (!pid || !/^\d{5}$/.test(pid)) {
      newErrors.providerId = "Provider ID must be exactly 5 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      // Prepare provider data
      const providerData = {
        lastName: formData.lastName.trim(),
        firstName: formData.firstName.trim(),
        title: formData.title.trim() || undefined,
        suffix: formData.suffix.trim() || undefined,
        department: formData.department.trim() || undefined,
        name: formData.name.trim() || undefined,
        facility: formData.facility.trim() || undefined,
        facilityAddress: formData.facilityAddress.trim() || undefined,
        contactNumber: formData.contactNumber.trim() || undefined,
      };

      // Only include providerId if it's provided (for edit mode or if user wants to set it)
      if (formData.providerId.trim()) {
        providerData.providerId = formData.providerId.trim();
      }

      if (isEditMode) {
        await updateProvider(provider.providerId, providerData);
      } else {
        await createProvider(providerData);
      }

      onSave();
    } catch (error) {
      console.error("Error saving provider:", error);
      setErrorMessage(
        `Failed to ${
          isEditMode ? "update" : "create"
        } provider. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="provider-dialog-title"
    >
      <DialogTitle
        id="provider-dialog-title"
        sx={{ color: maroon, fontWeight: 700 }}
      >
        {isEditMode ? "Edit Provider" : "Add New Provider"}
      </DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* First Name - Required */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={handleChange("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>

            {/* Last Name - Required */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>

            {/* Title */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Title"
                value={formData.title}
                onChange={handleChange("title")}
                placeholder="Dr., Mr., Ms., etc."
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Suffix */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Suffix"
                value={formData.suffix}
                onChange={handleChange("suffix")}
                placeholder="NP, MD, etc."
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Full Name */}
            <Grid item xs={12}>
              <TextField
                label="Full Name"
                value={formData.name}
                onChange={handleChange("name")}
                placeholder="Will be auto-generated if left empty"
                fullWidth
                disabled={loading}
                helperText="If empty, will be generated from title, first name, last name, and suffix"
              />
            </Grid>

            {/* Department */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Department"
                value={formData.department}
                onChange={handleChange("department")}
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Provider ID */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Provider ID"
                value={formData.providerId}
                onChange={handleChange("providerId")}
                error={!!errors.providerId}
                helperText={
                  errors.providerId ||
                  (isEditMode
                    ? "Provider ID cannot be changed"
                    : "Enter a 5-digit number. Auto-generated next ID is provided.")
                }
                fullWidth
                required
                disabled={loading || isEditMode}
              />
            </Grid>

            {/* Facility */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Facility"
                value={formData.facility}
                onChange={handleChange("facility")}
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Contact Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Number"
                value={formData.contactNumber}
                onChange={handleChange("contactNumber")}
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Facility Address */}
            <Grid item xs={12}>
              <TextField
                label="Facility Address"
                value={formData.facilityAddress}
                onChange={handleChange("facilityAddress")}
                fullWidth
                multiline
                rows={2}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            color: maroon,
          }}
        >
          CANCEL
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            backgroundColor: maroon,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#4a0000",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {loading
            ? "SAVING..."
            : isEditMode
            ? "UPDATE PROVIDER"
            : "CREATE PROVIDER"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditProvider;
